import { CSSProperties, useEffect, useRef, useState } from 'react';

import { CopyrightComponent } from '@vivli/shared/components';
import { AcceptedDataRequestInviteComponent } from './accepted-data-request-invite.component';
import { Styles } from '@vivli/shared/theme';
import { first } from 'rxjs/operators';
import { ButtonView, LoadIndicatorCenteredView } from '@vivli/shared/views';
import { useAuthEventsContext, useAuthServiceContext, useDataRequestsApiContext } from '@vivli/core/infrastructure/context';

const center500Style: CSSProperties = {
    textAlign: 'center',
    width: 500
};

const getEmail = () => {
    return decodeURIComponent(window.location.search.split('&')[0].split('=')[1]);
};

interface AcceptDataRequestInviteModalProps {
    dataRequestId: string;
    teamMemberId: string;
}

export const AcceptDataRequestInviteModal = ({ dataRequestId, teamMemberId }: AcceptDataRequestInviteModalProps) => {
    const dataRequestsService = useDataRequestsApiContext();
    const authService = useAuthServiceContext();
    const authEvents = useAuthEventsContext();
    const email = getEmail();

    const [accepted, setAccepted] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [error, setError] = useState(null);
    // Guard against re-entry: a successful sign-up now emits both 'signup-success' and (after the
    // follow-up silent login) 'login-success', so this handler can fire twice. A ref is used because
    // the auth subscription closes over first-render state and would not see setIsAccepting updates.
    const acceptingRef = useRef(false);

    const acceptDataRequestInvite = () => {
        if (acceptingRef.current) {
            return;
        }
        acceptingRef.current = true;
        setIsAccepting(true);
        dataRequestsService
            .acceptDataRequestInvite(dataRequestId, teamMemberId)
            .pipe(first())
            .subscribe(
                () => {
                    setAccepted(true);
                    setIsAccepting(false);
                },
                (error) => {
                    acceptingRef.current = false;
                    setAccepted(false);
                    setError(error);
                    setIsAccepting(false);
                }
            );
    };

    useEffect(() => {
        const authSub = authEvents.subscribe((event) => {
            switch (event) {
                case 'signup-success':
                case 'login-success':
                    acceptDataRequestInvite();
                    break;
            }
        });

        return () => {
            authSub.unsubscribe();
        };
    }, []);

    if (isAccepting) {
        return <LoadIndicatorCenteredView />;
    } else if (accepted) {
        return <AcceptedDataRequestInviteComponent />;
    }

    return (
        <div style={center500Style}>
            Congratulations, you're almost done with accepting the invitation.
            <br />
            It looks like you haven't logged in yet, you'll need to do that now.
            <br />
            <br />
            Be sure to sign up with the email address <strong>{email}</strong> or your acceptance of this invitation will not work.
            <br />
            <br />
            <br />
            <ButtonView className="adr_createAccount" style={{ ...Styles.Button.BUTTON_VIVLI_BLUE }} onClick={() => authService.signUp()}>
                Create Account
            </ButtonView>
            <br />
            <br />
            <ButtonView
                className="adr_existingAccount"
                style={{ ...Styles.Button.BUTTON_LIGHT_VIVLI_BLUE }}
                onClick={() => authService.login()}
            >
                Existing Account
            </ButtonView>
            {error && (
                <div>
                    <div style={{ color: 'red' }}>
                        <br />
                        Error: {error}
                    </div>
                </div>
            )}
            <CopyrightComponent />
        </div>
    );
};
